/**
 * Generic utility for streaming Server-Sent Events (SSE) from AI endpoints.
 * Handles the common pattern of parsing SSE data and yielding text chunks or structured events.
 */

export type SSEEvent =
  | { type: "text"; text: string }
  | { type: "summary"; summary: string }
  | { type: "metadata"; chat_id: string }
  | { type: "error"; error: string; code?: string; message?: string }
  | { type: "validated"; text: string }
  | { type: "tool_call"; label: string };

export const SSE_ERROR_CODES = [
  "service_unavailable",
  "rate_limited",
  "timeout",
  "bad_output",
  "fatal",
  "unknown",
] as const;

export type SSEErrorCode = (typeof SSE_ERROR_CODES)[number];

function toSSEErrorCode(value: unknown): SSEErrorCode {
  return SSE_ERROR_CODES.includes(value as SSEErrorCode)
    ? (value as SSEErrorCode)
    : "unknown";
}

/**
 * Error thrown when the SSE stream emits a structured error chunk.
 * Carries the backend-provided error `code` and a user-facing `message`
 * so callers can render a friendly UI instead of the raw exception text.
 */
export class SSEStreamError extends Error {
  readonly code: SSEErrorCode;
  readonly userMessage: string;

  constructor(opts: {
    code: SSEErrorCode;
    userMessage: string;
    technicalMessage: string;
  }) {
    super(opts.technicalMessage);
    this.name = "SSEStreamError";
    this.code = opts.code;
    this.userMessage = opts.userMessage;
  }
}

/**
 * Options for streaming SSE responses.
 */
export interface StreamSSEOptions {
  /** AbortSignal for cancellation */
  signal?: AbortSignal;
  /** Custom error message prefix */
  errorMessage?: string;
  /** Whether to yield summary events (for patient Q&A that needs summary handling) */
  yieldSummaryEvents?: boolean;
  /** Whether to yield tool_call events (an agent announcing a lookup) */
  yieldToolCallEvents?: boolean;
}

/**
 * Generic function to stream SSE responses from a fetch Response.
 * Handles parsing SSE format and yielding text chunks.
 * By default, yields plain strings. Set yieldEvents=true to get structured events.
 *
 * @param response - The fetch Response object
 * @param options - Streaming options
 * @returns AsyncGenerator yielding text strings (default) or SSE events (if yieldEvents=true)
 */
export async function* streamSSE(
  response: Response,
  options: StreamSSEOptions = {},
): AsyncGenerator<
  | string
  | { type: "summary"; summary: string }
  | { type: "metadata"; chat_id: string }
  | { type: "validated"; text: string }
  | { type: "tool_call"; label: string },
  void,
  unknown
> {
  const {
    errorMessage = "Failed to stream response",
    yieldSummaryEvents = false,
    yieldToolCallEvents = false,
  } = options;

  if (!response.ok) {
    const error = (await response
      .json()
      .catch(() => ({ error: "Unknown error" }))) as { error?: string };
    throw new Error(error.error || errorMessage);
  }

  if (!response.body) {
    throw new Error("No response body");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");

      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmedLine = line.trim();

        if (!trimmedLine || !trimmedLine.startsWith("data: ")) {
          continue;
        }

        const data = trimmedLine.slice(6);

        if (data === "[DONE]") {
          return;
        }

        try {
          const parsed = JSON.parse(data);

          // Handle error events — surface backend-provided code + user message
          // so the UI can render a friendly state rather than the raw exception.
          if (parsed.type === "error" && (parsed.error || parsed.message)) {
            const code = toSSEErrorCode(parsed.code);
            const userMessage =
              typeof parsed.message === "string" && parsed.message
                ? parsed.message
                : errorMessage;
            const technicalMessage =
              typeof parsed.error === "string" ? parsed.error : userMessage;
            throw new SSEStreamError({
              code,
              userMessage,
              technicalMessage,
            });
          }

          // Handle metadata events (e.g., chat_id)
          if (parsed.type === "metadata" && parsed.chat_id) {
            yield { type: "metadata", chat_id: String(parsed.chat_id) };
            continue;
          }

          // Handle summary events
          if (parsed.type === "summary" && parsed.summary) {
            if (yieldSummaryEvents) {
              const summaryText =
                typeof parsed.summary === "string"
                  ? parsed.summary
                  : String(parsed.summary || "");
              yield { type: "summary", summary: summaryText };
            }
            // Skip summary events when not yielding them (they're handled by the caller or not needed)
            continue;
          }

          if (parsed.type === "tool_call") {
            if (yieldToolCallEvents && typeof parsed.label === "string") {
              yield { type: "tool_call", label: parsed.label };
            }
            continue;
          }

          // Handle validated events (citations validated against context)
          if (parsed.type === "validated" && parsed.text != null) {
            const validatedText =
              typeof parsed.text === "string"
                ? parsed.text
                : String(parsed.text);
            yield { type: "validated", text: validatedText };
            continue;
          }

          // Handle text chunks
          if (parsed.type === "text" && parsed.text != null) {
            const textValue =
              typeof parsed.text === "string"
                ? parsed.text
                : String(parsed.text);
            yield textValue;
          } else if (parsed.text != null && !parsed.type) {
            // Old format without type field (backward compatibility)
            const textValue =
              typeof parsed.text === "string"
                ? parsed.text
                : String(parsed.text);
            yield textValue;
          }
        } catch (parseError) {
          if (parseError instanceof SyntaxError) {
            // Skip malformed SSE data
            console.warn("Skipping malformed SSE data:", data);
          } else {
            throw parseError;
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
